import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MessageCircle, Plane, Briefcase, Users, Award, ArrowRight, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { chatAPI } from '@/services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [selectedGoal, setSelectedGoal] = useState('');

  // Languages
  const languages = [
    { value: 'English', flag: '🇬🇧', label: 'Inglês' },
    { value: 'Spanish', flag: '🇪🇸', label: 'Espanhol' },
    { value: 'French', flag: '🇫🇷', label: 'Francês' },
    { value: 'German', flag: '🇩🇪', label: 'Alemão' },
    { value: 'Chinese', flag: '🇨🇳', label: 'Chinês' },
  ];

  // Levels
  const levels = [
    { value: 'beginner', emoji: '🌱', label: 'Iniciante' },
    { value: 'intermediate', emoji: '🌿', label: 'Intermediário' },
    { value: 'advanced', emoji: '🌳', label: 'Avançado' },
  ];

  // Goals
  const goals = [
    { value: 'travel', icon: Plane, label: 'Viagem', color: 'bg-blue-50 border-blue-200 text-blue-600' },
    { value: 'work', icon: Briefcase, label: 'Trabalho', color: 'bg-purple-50 border-purple-200 text-purple-600' },
    { value: 'conversation', icon: Users, label: 'Conversação', color: 'bg-green-50 border-green-200 text-green-600' },
    { value: 'interview', icon: Award, label: 'Entrevistas', color: 'bg-orange-50 border-orange-200 text-orange-600' },
  ];

  const handleLanguageSelect = (lang) => {
    setSelectedLanguage(lang);
    setTimeout(() => setStep(2), 200);
  };

  const handleLevelSelect = (level) => {
    setSelectedLevel(level);
    setTimeout(() => setStep(3), 200);
  };

  const handleGoalSelect = async (goal) => {
    setSelectedGoal(goal);
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      // Save onboarding data
      await axios.put(
        `${API}/auth/complete-onboarding`,
        {
          learning_language: selectedLanguage,
          level: selectedLevel,
          goal: goal
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Award badge
      try {
        await axios.post(
          `${API}/gamification/award-badge`,
          { badge_id: 'first_steps' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (e) {}
      
      await refreshUser();
      
      // Celebration
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      // Show success step briefly
      setStep(4);
      
      // Start first conversation automatically
      setTimeout(async () => {
        try {
          const conversation = await chatAPI.createConversation('free_chat', goal);
          toast.success('🎉 Vamos conversar!');
          navigate(`/chat/${conversation.id}?welcome=true`);
        } catch (error) {
          navigate('/dashboard?welcome=true');
        }
      }, 1500);
      
    } catch (error) {
      toast.error('Erro ao salvar. Tente novamente.');
      setSaving(false);
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'amigo';
  const totalSteps = 3;

  return (
    <div className="min-h-screen bg-sand flex flex-col">
      {/* Progress Bar */}
      {step <= 3 && (
        <div className="px-6 pt-6">
          <div className="flex gap-2 max-w-md mx-auto">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                  s <= step ? 'bg-forest' : 'bg-forest/20'
                }`}
              />
            ))}
          </div>
          <p className="text-center text-xs text-stone mt-2">
            Passo {step} de {totalSteps}
          </p>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <AnimatePresence mode="wait">
          
          {/* Step 1: Language */}
          {step === 1 && (
            <motion.div
              key="language"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-sm"
            >
              <div className="text-center mb-8">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-16 h-16 bg-lime/30 rounded-2xl flex items-center justify-center mx-auto mb-4"
                >
                  <MessageCircle className="w-8 h-8 text-forest" />
                </motion.div>
                <h1 className="font-fraunces text-2xl font-semibold text-forest mb-1">
                  Olá, {firstName}! 👋
                </h1>
                <p className="text-stone text-sm">
                  Qual idioma você quer aprender?
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {languages.map((lang, i) => (
                  <motion.button
                    key={lang.value}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => handleLanguageSelect(lang.value)}
                    className={`p-4 rounded-2xl border-2 transition-all active:scale-95 ${
                      selectedLanguage === lang.value
                        ? 'bg-lime/20 border-lime'
                        : 'bg-white border-transparent hover:border-forest/20'
                    }`}
                    data-testid={`lang-${lang.value.toLowerCase()}`}
                  >
                    <span className="text-3xl block mb-1">{lang.flag}</span>
                    <span className="text-sm font-medium text-forest">{lang.label}</span>
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 2: Level */}
          {step === 2 && (
            <motion.div
              key="level"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-sm"
            >
              <div className="text-center mb-8">
                <h1 className="font-fraunces text-2xl font-semibold text-forest mb-1">
                  Qual seu nível?
                </h1>
                <p className="text-stone text-sm">
                  Não se preocupe, você pode mudar depois
                </p>
              </div>

              <div className="space-y-3">
                {levels.map((level, i) => (
                  <motion.button
                    key={level.value}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => handleLevelSelect(level.value)}
                    className={`w-full p-4 rounded-2xl border-2 flex items-center gap-4 transition-all active:scale-98 ${
                      selectedLevel === level.value
                        ? 'bg-lime/20 border-lime'
                        : 'bg-white border-transparent hover:border-forest/20'
                    }`}
                    data-testid={`level-${level.value}`}
                  >
                    <span className="text-3xl">{level.emoji}</span>
                    <span className="font-medium text-forest text-left">{level.label}</span>
                    {selectedLevel === level.value && (
                      <Check className="w-5 h-5 text-forest ml-auto" />
                    )}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          )}

          {/* Step 3: Goal */}
          {step === 3 && (
            <motion.div
              key="goal"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-sm"
            >
              <div className="text-center mb-8">
                <h1 className="font-fraunces text-2xl font-semibold text-forest mb-1">
                  Por que você quer aprender?
                </h1>
                <p className="text-stone text-sm">
                  Vamos personalizar sua experiência
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {goals.map((goal, i) => {
                  const Icon = goal.icon;
                  return (
                    <motion.button
                      key={goal.value}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.05 }}
                      onClick={() => handleGoalSelect(goal.value)}
                      disabled={saving}
                      className={`p-5 rounded-2xl border-2 transition-all active:scale-95 ${goal.color} ${
                        selectedGoal === goal.value ? 'ring-2 ring-forest ring-offset-2' : ''
                      } ${saving ? 'opacity-50' : ''}`}
                      data-testid={`goal-${goal.value}`}
                    >
                      <Icon className="w-8 h-8 mx-auto mb-2" />
                      <span className="font-medium text-sm block">{goal.label}</span>
                      {saving && selectedGoal === goal.value && (
                        <div className="mt-2">
                          <div className="w-4 h-4 border-2 border-current/20 border-t-current rounded-full animate-spin mx-auto" />
                        </div>
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Step 4: Success - Auto redirects */}
          {step === 4 && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <motion.div
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.5, repeat: Infinity }}
                className="w-20 h-20 bg-lime rounded-full flex items-center justify-center mx-auto mb-6"
              >
                <Check className="w-10 h-10 text-forest" />
              </motion.div>
              <h1 className="font-fraunces text-2xl font-semibold text-forest mb-2">
                Tudo pronto! 🎉
              </h1>
              <p className="text-stone">
                Iniciando sua primeira conversa...
              </p>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
};

export default OnboardingPage;
